import React from 'react';
import { useTranslation } from 'react-i18next';
import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import WsTemplatePreviewMC from './WsTemplatePreviewMC';
import { copyCodeButtonLabel, isAuthenticationTemplate } from './utils/templateTextUtils';

function parseTemplateVariables(templateText) {
  // Extract variables from template text like {{1}}, {{2}}, etc.
  const variables = templateText.match(/\{\{\d+\}\}/g) || [];
  const unique = Array.from(new Set(variables));
  unique.sort((a, b) => {
    const nA = parseInt(a.replace(/[^\d]/g, ''), 10);
    const nB = parseInt(b.replace(/[^\d]/g, ''), 10);
    return nA - nB;
  });
  return unique;
}

function extractExampleValues(example, templateText) {
  if (!example || !templateText) return [];
  
  const variables = parseTemplateVariables(templateText);
  const values = [];
  
  let workingExample = example;
  let workingTemplate = templateText;
  
  variables.forEach((variable, index) => {
    // Find what replaced this variable in the example
    const templateParts = workingTemplate.split(variable);
    if (templateParts.length === 2) {
      const beforeVar = templateParts[0];
      const afterVar = templateParts[1];
      
      const beforeIndex = workingExample.indexOf(beforeVar) + beforeVar.length;
      const afterIndex = workingExample.indexOf(afterVar, beforeIndex);
      
      if (afterIndex > beforeIndex) {
        const value = workingExample.substring(beforeIndex, afterIndex);
        values.push(value);

        workingExample = workingExample.replace(value, variable);
        workingTemplate = workingTemplate.replace(variable, variable);
      } else {
        values.push('');
      }
    } else {
      values.push('');
    }
  });
  
  return values;
}

function extractButtonsFromText(text) {
  const quickReplies = [];
  const callToActions = [];
  let authButton = { text: '' };
  
  if (!text) return { quickReplies, callToActions, authButton, cleanedText: text };

  const parts = text.split('|').map(part => part.trim());
  const cleanedText = parts[0].trim();

  parts.slice(1).forEach((buttonPart, index) => {
    // First check if it's a URL or phone pattern (has http/https/tel or looks like URL/phone)
    // Pattern: [Button Text, URL] where URL contains http/https/tel or has . or starts with +
    const urlMatch = buttonPart.match(/\[([^,]+),\s*([^\]]+)\]/);
    
    if (urlMatch) {
      const [, buttonText, potentialUrl] = urlMatch;
      const url = potentialUrl.trim();

      if (url.includes('http') || url.includes('tel:') || url.includes('.') || url.match(/^\+?\d+/) || url.includes('www')) {
        // Determine if it's a phone number or URL
        if (url.includes('tel:') || url.match(/^\+?\d+/)) {
          callToActions.push({
            index,
            type: 'PHONE_NUMBER',
            text: buttonText.trim(),
            phone_number: url.replace('tel:', '').trim()
          });
        } else {
          callToActions.push({
            index,
            type: 'URL',
            text: buttonText.trim(),
            url: url
          });
        }
      } else {
        // If it doesn't look like a URL, treat the whole thing as a quick reply button text
        const fullText = buttonPart.match(/\[([^\]]+)\]/);
        if (fullText) {
          quickReplies.push({
            index,
            text: fullText[1].trim()
          });
        }
      }
    } else {
      const simpleMatch = buttonPart.match(/\[([^\]]+)\]/);
      if (simpleMatch) {
        const [, buttonText] = simpleMatch;
        quickReplies.push({
          index,
          text: buttonText.trim()
        });
      }
    }
  });
  
  return { quickReplies, callToActions, authButton, cleanedText };
}

function castPreviewProps(wsTemplate = {}, fullText = null, showOnlyChatPreview = false) {
  let templateText = '';
  let quickReplies = [];
  let callToActions = [];
  let authButton = { text: '' };
  
  if (showOnlyChatPreview && wsTemplate.template && wsTemplate.links) {
    templateText = wsTemplate.template;
    const authTemplate = isAuthenticationTemplate(wsTemplate);

    if (!authTemplate) {
      quickReplies = wsTemplate.links.map((linkText, index) => ({
        index,
        text: linkText
      }));
    }

    return {
      type: wsTemplate.template_type || 'text',
      headerText: '',
      templateText,
      templateValues: [],
      quickReplies,
      callToActions: [],
      file: null,
      authButton: authTemplate ? { text: copyCodeButtonLabel() } : { text: '' },
      expirationOffer: false,
      headerOfferTitle: '',
      offerButtonText: '',
      offerButtonValue: '',
      offerUrlButtonText: '',
      expirationDate: null,
      cards: wsTemplate.cards || [],
    };
  }
  
  // Original logic for real WhatsApp template data
  if (fullText) {
    templateText = fullText;
  } else if (wsTemplate.example) {
    templateText = wsTemplate.example;
  } else if (wsTemplate.text) {
    templateText = wsTemplate.text;
  }
  
  // Extract buttons from text and get cleaned text
  const { quickReplies: extractedQuickReplies, callToActions: extractedCallToActions, authButton: extractedAuthButton, cleanedText } = extractButtonsFromText(templateText);
  
  // Use cleaned text (without button parts) for display
  const finalTemplateText = cleanedText || templateText;
  
  // For template values: if we're showing an example, extract values from it
  // Otherwise use empty array (no variables to replace)
  const templateValues = wsTemplate.example && wsTemplate.text && !fullText
    ? extractExampleValues(wsTemplate.example, wsTemplate.text)
    : [];
  
  // File handling: Always null for new view (no file upload interaction)
  // This will force the preview to show placeholders for non-text types
  // Change if we want to support file previews in the future
  const file = null;
  const authTemplate = isAuthenticationTemplate(wsTemplate);

  return {
    type: wsTemplate.template_type || 'text',
    headerText: wsTemplate.header_text || '',
    templateText: finalTemplateText,
    templateValues,
    quickReplies: authTemplate ? [] : extractedQuickReplies,
    callToActions: authTemplate ? [] : extractedCallToActions,
    file,
    authButton: authTemplate ? { text: copyCodeButtonLabel() } : extractedAuthButton,
    expirationOffer: wsTemplate.expiration_offer || false,
    headerOfferTitle: wsTemplate.header_offer_title || '',
    offerButtonText: wsTemplate.offer_button_text || '',
    offerButtonValue: wsTemplate.offer_button_value || '',
    offerUrlButtonText: wsTemplate.offer_url_button_text || '',
    expirationDate: wsTemplate.expiry_minutes && wsTemplate.expiry_minutes > 0
      ? new Date(Date.now() + wsTemplate.expiry_minutes * 60 * 1000)
      : null,
    cards: wsTemplate.cards || [],
  };
}

const WsTemplatePreviewModalMC = ({
  wsTemplate,
  fullText,
  showOnlyChatPreview = false,
  onSelectAndReview
}) => {
  const { t } = useTranslation('WsTemplatesMC');
  const previewProps = castPreviewProps(wsTemplate, fullText, showOnlyChatPreview);
  const example = wsTemplate?.example;

  return (
    <RightModalLayout
      title={showOnlyChatPreview ? t('templatesModal.buttons.preview') : (wsTemplate?.name || t('templatesModal.buttons.preview'))}
      footerVariant="HORIZONTAL"
      footerClassName="tw-border-none"
      submitLabel={showOnlyChatPreview && onSelectAndReview ? t('templatesModal.buttons.selectAndReview') : undefined}
      submitAction={showOnlyChatPreview && onSelectAndReview ? onSelectAndReview : undefined}
    >
      <WsTemplatePreviewMC 
        {...previewProps}
        example={example} 
        showOnlyChatPreview={showOnlyChatPreview}
      />
    </RightModalLayout>
  );
};

export default WsTemplatePreviewModalMC;
